import { DropStatus, FeeType } from "@anaconda/core/drop/types"
import { UNKNOWN_CURRENCY } from "@anaconda/core/price/types"
import { msg } from "@lingui/core/macro"
import { action, computed } from "@reatom/core"
import { molecule } from "bunshi"
import { DropIdScope, DropScope } from "./scope"

const MINIMUM_DISPLAY_PRICE = 0.0001

/**
 * Lightweight molecule for calculating drop display information
 * WITHOUT fetching any API data or minted counts.
 *
 * Use this for grid views and spotlight where we don't need real-time minted data.
 * For detail pages with minting functionality, use DropMoleculeReatom instead.
 */
export const DropDisplayMoleculeReatom = molecule((mol, scope) => {
  const dropAtom = scope(DropScope)
  const { dropId } = scope(DropIdScope)

  if (!dropAtom) {
    throw new Error(
      '"DropDisplayMoleculeReatom" must be used within "DropScope"',
    )
  }

  // Get current phase based on current time
  // Note: Uses Date.now() directly - no reactive updates
  // This is fine for grid items which don't need real-time status changes
  const currentPhaseAtom = computed(() => {
    const now = Date.now()
    const phases = dropAtom().phases() || []

    if (phases.length === 0) {
      return null
    }

    // Find active phase
    const activePhase = phases.find((phase) => {
      const startDate = new Date(phase.startDate).getTime()
      const endDate = phase.endDate
        ? new Date(phase.endDate).getTime()
        : undefined
      return startDate <= now && (!endDate || now < endDate)
    })

    // Return active phase or first phase as fallback
    return activePhase || phases[0]
  }, `currentPhaseAtom-${dropId}`)

  // Calculate drop status based on phases only (no minted data)
  const dropStatusAtom = computed(() => {
    const now = Date.now()
    const phases = dropAtom().phases() || []

    if (phases.length === 0) {
      return DropStatus.Ended
    }

    const firstPhase = phases[0]
    const lastPhase = phases[phases.length - 1]

    // Check if upcoming
    if (
      firstPhase?.startDate &&
      now < new Date(firstPhase.startDate).getTime()
    ) {
      return DropStatus.Upcoming
    }

    // Check if ended
    if (lastPhase?.endDate && now >= new Date(lastPhase.endDate).getTime()) {
      return DropStatus.Ended
    }

    // Otherwise it's minting
    return DropStatus.Minting
  }, `dropStatusAtom-${dropId}`)

  // Get status text
  const dropStatusTextAtom = computed(() => {
    const status = dropStatusAtom()

    switch (status) {
      case DropStatus.Upcoming:
        return msg`Upcoming`
      case DropStatus.Ended:
        return msg`Ended`
      case DropStatus.Minting:
        return msg`Minting now`
      case DropStatus.OverSupply:
        return msg`Ended`
      default:
        return msg`Ended`
    }
  }, `dropStatusTextAtom-${dropId}`)

  // Calculate fees for current phase
  const dropFeeAtom = computed(() => {
    const currentPhase = currentPhaseAtom()

    if (!currentPhase) {
      return {
        amount: "0",
        currency: UNKNOWN_CURRENCY,
      }
    }

    const fees = dropAtom().fees() || []
    const feesAmount = fees.reduce((acc: number, fee) => {
      const feeValue =
        fee.type === FeeType.FLAT
          ? fee.amount
          : fee.amount * Number(currentPhase.price.amount)

      return acc + feeValue
    }, 0)

    return {
      ...currentPhase.price,
      amount: feesAmount.toString(),
    }
  }, `dropFeeAtom-${dropId}`)

  // Calculate display price (price without fees)
  const dropDisplayPriceAtom = computed(() => {
    const currentPhase = currentPhaseAtom()
    const fee = dropFeeAtom()

    if (!currentPhase) {
      return {
        amount: "0",
        currency: UNKNOWN_CURRENCY,
      }
    }

    const priceWithoutFees =
      Number(currentPhase.price.amount) - Number(fee.amount)
    const displayAmount =
      priceWithoutFees < MINIMUM_DISPLAY_PRICE
        ? "0"
        : priceWithoutFees.toString()

    return { ...currentPhase.price, amount: displayAmount }
  }, `dropDisplayPriceAtom-${dropId}`)

  return {
    currentPhaseAtom,
    dropStatusAtom,
    dropStatusTextAtom,
    dropFeeAtom,
    dropDisplayPriceAtom,
  }
})
